/**
 * @file Two-pane resizable, mirrorable split layout — design §4.1.
 * @module @labcraft/lab/ui/components/split-layout
 *
 * Hand-rolled CSS-grid splitter (no library dep) with three columns:
 * the two content columns plus a zero-width divider between them. The
 * middle column is the divider — zero width with a `::before` pseudo
 * extending ±3px on each side for a generous click target. Dragging
 * the divider clamps to 30–70% per the design spec; double-click
 * resets to ~58% content. The chosen percent persists to
 * `localStorage['labcraft.splitPercent']`.
 *
 * **Mirrorable.** The two panes carry fixed roles — `content` and
 * `terminal` — and the layout decides which sits on the left from the
 * `orientation` prop (`'content-left'` | `'content-right'`). Mirroring
 * swaps **both** the DOM child order and the `grid-template-columns`
 * track sizes together, while each pane keeps a **stable React key**
 * (`'content'` / `'terminal'`). React reconciles by key, so the
 * terminal/xterm subtree is *moved* in the DOM rather than unmounted —
 * the live WebSocket + xterm buffer survive the flip. We deliberately
 * do NOT use CSS `order` alone (which would leave the divider drag math
 * and the persisted percent ambiguous about which side `percent` sizes)
 * — DOM order + columns move in lockstep so `percent` always sizes the
 * *content* pane regardless of which side it's on. Orientation persists
 * to `localStorage['labcraft.splitOrientation']`.
 *
 * Why hand-rolled: the prototype uses the same shape, the brand has
 * specific hover affordances (three-dot grip, faint accent stripe),
 * and adding a library dep for one resizer is overkill.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react'
import styles from './split_layout.module.css'

/** localStorage key for the persisted content-pane split percent. */
const PERCENT_KEY = 'labcraft.splitPercent'

/**
 * localStorage key for the persisted pane orientation. The Settings
 * "Default layout" control and the app-bar mirror toggle both drive the
 * reader's controlled `orientation` (lifted into `ReaderChromeContext`);
 * this layout persists every change here so a refresh restores the
 * learner's last orientation, and both controls read it back on mount.
 */
const ORIENTATION_KEY = 'labcraft.splitOrientation'

/** Minimum content-pane width as a percent of the split. */
const MIN_PERCENT = 30

/** Maximum content-pane width as a percent of the split. */
const MAX_PERCENT = 70

/** Double-click reset target — ~58% content per the design spec. */
const DEFAULT_PERCENT = 58

/**
 * Which pane sits on the left. `content-left` is the default reading
 * orientation; `content-right` mirrors it (terminal on the left).
 */
export type SplitOrientation = 'content-left' | 'content-right'

/**
 * Props for {@link SplitLayout}.
 *
 * @property content - The content pane (chapter prose). Always keyed
 *   `'content'` internally so it survives mirror flips.
 * @property terminal - The terminal/workspace pane. Always keyed
 *   `'terminal'` internally so its xterm subtree survives mirror flips.
 * @property orientation - Which pane sits on the left. Controlled by
 *   the caller (the reader app-bar mirror toggle owns the state); the
 *   layout persists changes but doesn't own them, so the toggle's
 *   button label can reflect the live value.
 */
export type SplitLayoutProps = {
  content: ReactNode
  terminal: ReactNode
  orientation: SplitOrientation
}

/**
 * Resizable, mirrorable two-pane split. See the file header for the
 * key-stability mechanism that keeps the terminal subtree alive across
 * a mirror flip.
 *
 * @param props - {@link SplitLayoutProps}.
 * @returns The split-layout grid element.
 */
export function SplitLayout(props: SplitLayoutProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [percent, setPercent] = useState<number>(() => readStoredPercent())
  const [dragging, setDragging] = useState(false)

  // Persist percent on every change. `sessionStorage` would be wrong
  // here — pane preferences should survive tab close (per design).
  useEffect(() => {
    try {
      localStorage.setItem(PERCENT_KEY, String(percent))
    } catch {
      // Storage may be unavailable (private mode, quota); persistence
      // is a nice-to-have, not load-bearing.
    }
  }, [percent])

  // Persist orientation whenever the controlled prop changes. The
  // caller owns the state; the layout owns the durable mirror of it so
  // a refresh restores the user's last orientation.
  useEffect(() => {
    try {
      localStorage.setItem(ORIENTATION_KEY, props.orientation)
    } catch {
      // See above — best-effort.
    }
  }, [props.orientation])

  // Drag state lives on window (not the divider) so the user can drag
  // past the divider without losing the gesture. `percent` always
  // sizes the *content* pane, so when content is on the right we invert
  // the cursor-derived raw percent before clamping.
  useEffect(() => {
    if (!dragging) return
    const contentOnLeft = props.orientation === 'content-left'
    const onMove = (e: MouseEvent): void => {
      const node = containerRef.current
      if (!node) return
      const rect = node.getBoundingClientRect()
      if (rect.width === 0) return
      const fromLeft = ((e.clientX - rect.left) / rect.width) * 100
      const raw = contentOnLeft ? fromLeft : 100 - fromLeft
      const clamped = Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, raw))
      setPercent(clamped)
    }
    const onUp = (): void => setDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    // Lock the body cursor + suppress text selection while dragging.
    const prevCursor = document.body.style.cursor
    const prevUserSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.cursor = prevCursor
      document.body.style.userSelect = prevUserSelect
    }
  }, [dragging, props.orientation])

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(true)
  }, [])

  const onDoubleClick = useCallback(() => {
    setPercent(DEFAULT_PERCENT)
  }, [])

  // Both panes carry stable keys so React moves (not remounts) the
  // terminal subtree when the order flips. The divider lives between
  // them as a separate keyed child.
  const contentOnLeft = props.orientation === 'content-left'
  const contentPane = (
    <div key="content" className={styles.contentPane}>
      {props.content}
    </div>
  )
  const terminalPane = (
    <div key="terminal" className={styles.terminalPane}>
      {props.terminal}
    </div>
  )
  const divider = (
    <div
      key="divider"
      className={
        dragging
          ? `${styles.divider} ${styles.dividerDragging}`
          : styles.divider
      }
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={MIN_PERCENT}
      aria-valuemax={MAX_PERCENT}>
      <div className={styles.grip} aria-hidden>
        <span className={styles.dot} />
        <span className={styles.dot} />
        <span className={styles.dot} />
      </div>
    </div>
  )

  // `percent` always sizes the content pane. The column order tracks
  // the DOM order so the two stay coherent.
  const cols = contentOnLeft
    ? `${percent}fr 0 ${100 - percent}fr`
    : `${100 - percent}fr 0 ${percent}fr`
  const children = contentOnLeft
    ? [contentPane, divider, terminalPane]
    : [terminalPane, divider, contentPane]

  return (
    <div
      ref={containerRef}
      className={styles.split}
      style={{ gridTemplateColumns: cols }}>
      {children}
    </div>
  )
}

/**
 * Read + clamp the persisted split percent. Always returns a number in
 * `[MIN_PERCENT, MAX_PERCENT]`; falls back to the default if the stored
 * value is missing or malformed. A module helper (not a class static)
 * matching this file's existing React-adjacent precedent; the
 * TypeScript rule exempts React components/hooks and their local
 * helpers from the functions-on-classes mandate.
 *
 * @returns The clamped content-pane percent.
 */
function readStoredPercent(): number {
  try {
    const raw = localStorage.getItem(PERCENT_KEY)
    if (!raw) return DEFAULT_PERCENT
    const num = Number(raw)
    if (!Number.isFinite(num)) return DEFAULT_PERCENT
    return Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, num))
  } catch {
    return DEFAULT_PERCENT
  }
}

/**
 * Read the persisted orientation. Falls back to `'content-left'` (the
 * default reading orientation) when missing or malformed. Exported so
 * the reader page can seed its controlled `orientation` state from the
 * last-used value on mount.
 *
 * @returns The stored {@link SplitOrientation}.
 */
export function readStoredOrientation(): SplitOrientation {
  try {
    const raw = localStorage.getItem(ORIENTATION_KEY)
    return raw === 'content-right' ? 'content-right' : 'content-left'
  } catch {
    return 'content-left'
  }
}
